import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Share2 } from "lucide-react";

/**
 * DailyView — sacred home: a devotional page resting on top of an old Bible page.
 *
 *   FOR TODAY
 *   ─── divider · ◆ ─
 *
 *   Main scripture (elegant, not oversized)
 *
 *   Isaiah 35:4 (bigger, darker reference)
 *
 *   ╭ ─ ◌ ─ ─ ─ ◌ ─ ─ ╮      ← organic pen-circle loops 2-3x
 *   │   short reflection   │
 *   │   ~4-5 lines max     │
 *   ╰ ─ ─ ─ ◌ ─ ─ ─ ─ ─ ╯
 */
export const DailyView = ({ verse, onOpenMenu, isFavorited, onToggleFavorite, onShare }) => {
  // Long-press save heart — invisible by default
  const [heartVisible, setHeartVisible] = useState(false);
  const longPressTimer = useRef(null);
  const hideTimer = useRef(null);
  const pressOriginRef = useRef({ x: 0, y: 0 });

  const clearLong = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };
  const scheduleHide = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setHeartVisible(false), 2000);
  };

  const handlePointerDown = (e) => {
    pressOriginRef.current = { x: e.clientX || 0, y: e.clientY || 0 };
    clearLong();
    longPressTimer.current = setTimeout(() => {
      setHeartVisible(true);
      scheduleHide();
    }, 500);
  };
  const handlePointerMove = (e) => {
    if (!longPressTimer.current) return;
    const dx = (e.clientX || 0) - pressOriginRef.current.x;
    const dy = (e.clientY || 0) - pressOriginRef.current.y;
    if (dx * dx + dy * dy > 64) clearLong();
  };
  const handlePointerUp = () => clearLong();
  const handlePointerCancel = () => clearLong();

  useEffect(() => () => {
    clearLong();
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, []);

  const handleHeartTap = (e) => {
    e.stopPropagation();
    onToggleFavorite?.(verse);
    scheduleHide();
  };

  if (!verse) return <div data-testid="daily-view" />;

  // Daily home shows the FULL reflection inside the pen circle.
  // The SVG adapts to height via ResizeObserver — every word is enclosed.
  const reflectionParas = verse.explanation
    ? [verse.explanation.trim()]
    : [];

  return (
    <div
      className="mx-auto flex flex-col"
      style={{
        width: "100%",
        maxWidth: "420px",
        padding: "calc(20px + env(safe-area-inset-top)) 28px env(safe-area-inset-bottom)",
      }}
      data-testid="daily-view"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onPointerLeave={handlePointerCancel}
    >
      {/* TOP — FOR TODAY label + divider + diamond (also discreet menu trigger) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenMenu?.();
        }}
        data-testid="daily-header-tap"
        className="flex flex-col items-center ld-anim-header"
        style={{ alignSelf: "center", padding: 0, background: "transparent" }}
      >
        <span
          className="font-ui uppercase"
          data-testid="daily-header-label"
          style={{
            fontSize: "13px",
            letterSpacing: "0.36em",
            fontWeight: 700,
            color: "#221A10",
            marginBottom: "12px",
            padding: "8px 18px",
            borderRadius: "999px",
            position: "relative",
            textShadow:
              "0 0 10px rgba(255,250,235,1), 0 0 18px rgba(255,250,235,0.9), 0 0 30px rgba(255,250,235,0.7), 0 1px 0 rgba(255,250,235,0.95)",
            background:
              "radial-gradient(60% 110% at 50% 50%, rgba(255,250,235,0.92) 0%, rgba(255,250,235,0.72) 50%, rgba(255,250,235,0) 100%)",
          }}
        >
          For today
        </span>
        <span
          aria-hidden="true"
          style={{
            display: "block",
            width: "56px",
            height: "1px",
            backgroundColor: "#3D362B",
            opacity: 0.7,
            margin: "0 auto 8px auto",
          }}
        />
        <span
          aria-hidden="true"
          style={{
            display: "block",
            width: "6px",
            height: "6px",
            backgroundColor: "#3D362B",
            opacity: 0.55,
            transform: "rotate(45deg)",
            margin: "0 auto",
          }}
        />
      </button>

      {/* HERO SCRIPTURE — prominent like the master reference (5-6 lines wrap) */}
      <p
        className="font-garamond text-center ld-anim-scripture"
        data-testid="verse-text"
        style={{
          maxWidth: "340px",
          margin: "28px auto 0",
          fontSize: "clamp(20px, 5.6vw, 24px)",
          lineHeight: 1.32,
          color: "#05030A",
          fontWeight: 700,
          letterSpacing: "0.002em",
          textShadow: "0 1px 2px rgba(247, 244, 239, 0.65), 0 0 6px rgba(247, 244, 239, 0.4)",
        }}
      >
        {verse.text}
      </p>

      {/* REFERENCE — same serif weight, with tilde flourishes, sits right below */}
      <p
        className="font-garamond text-center ld-anim-reference"
        data-testid="verse-reference"
        style={{
          fontSize: "clamp(18px, 5.0vw, 22px)",
          color: "#05030A",
          fontWeight: 600,
          letterSpacing: "0.005em",
          margin: "10px auto 0",
          textShadow: "0 1px 2px rgba(247, 244, 239, 0.7)",
        }}
      >
        <span style={{ marginRight: "10px", opacity: 0.7 }}>~</span>
        {verse.reference}
        <span style={{ marginLeft: "10px", opacity: 0.7 }}>~</span>
      </p>

      {/* HIDDEN HEART — long-press reveal, tap to save */}
      <div
        className="relative flex justify-center"
        style={{ height: "30px" }}
        aria-hidden={!heartVisible}
      >
        <AnimatePresence>
          {heartVisible && (
            <motion.button
              key="save-heart"
              type="button"
              onClick={handleHeartTap}
              data-testid="hidden-save-heart"
              aria-label={isFavorited ? "Remove from saved" : "Save this word"}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.4 } }}
              transition={{ duration: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
              style={{
                position: "absolute",
                top: "6px",
                background: "transparent",
                padding: "2px",
                lineHeight: 0,
              }}
            >
              <Heart
                style={{ width: 18, height: 18 }}
                strokeWidth={1.4}
                color={isFavorited ? "#1B3478" : "#9B928A"}
                fill={isFavorited ? "#1B3478" : "transparent"}
              />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* PEN-CIRCLED REFLECTION — hand-drawn rectangle with signature corner loops */}
      <div className="ld-anim-box" style={{ marginTop: "4px" }}>
        <PenCircledReflection paragraphs={reflectionParas} />
      </div>

      {/* Tiny low-opacity share glyph — sacred-stillness friendly */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20px",
          minHeight: "44px",
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onShare?.(verse);
          }}
          data-testid="daily-share-button"
          aria-label="Share today's word"
          style={{
            background: "transparent",
            padding: "10px",
            borderRadius: "999px",
            opacity: 0.38,
            transition: "opacity 0.4s ease",
            lineHeight: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.38")}
        >
          <Share2 style={{ width: 16, height: 16 }} strokeWidth={1.3} color="#3D362B" />
        </button>
      </div>

      {/* Bottom breathing space */}
      <div style={{ minHeight: "20px" }} aria-hidden="true" />
    </div>
  );
};

/* ---------------------------------------------------------------- */
/* PenCircledReflection — a hand-drawn pen CIRCLE traced 3 times    */
/* around the reflection. Generous breathing room so the loop never */
/* crosses any letter. Per-segment jitter for an organic human feel.*/
/* ---------------------------------------------------------------- */
const PenCircledReflection = ({ paragraphs }) => {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 240, h: 180 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const obs = new ResizeObserver(([entry]) => {
      const r = entry.contentRect;
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Generous outside padding so the loop sits WELL clear of every letter.
  // Pad heavily on both axes so a standard ellipse (rx=W/2, ry=H/2) still
  // leaves ample blank space between text corners and the pen line.
  const padX = 44;   // horizontal gap from text edge to oval (tighter → wider text)
  const padY = 48;   // vertical gap from text edge to oval
  const W = size.w + padX * 2;
  const H = size.h + padY * 2;
  const cx = W / 2;
  const cy = H / 2;

  // Base radii — slightly smaller than W/2, H/2 so jitter stays inside SVG.
  // Math: with padX=60/padY=48 the ellipse corners clear text corners by
  // ~12% — verified safe for reflections up to ~6 lines.
  const baseRx = W / 2 - 6;
  const baseRy = H / 2 - 6;

  // Seeded pseudo-random for stable jitter across renders of the same size
  const seedRand = (seed) => {
    let s = seed | 0;
    return () => {
      s = (s * 1664525 + 1013904223) | 0;
      return ((s >>> 0) % 10000) / 10000;
    };
  };

  // Build a single jittered loop with its own center offset, tilt, scale and
  // wobble amplitude — so 3 passes look like 3 distinct human attempts rather
  // than three identical circles stacked.
  const buildLoop = (cfg) => {
    const { dx = 0, dy = 0, scaleX = 1, scaleY = 1, tilt = 0,
            startAngle = -95, sweepDeg = 365, jitter = 4, seed = 1 } = cfg;
    const cosT = Math.cos((tilt * Math.PI) / 180);
    const sinT = Math.sin((tilt * Math.PI) / 180);
    const r1Base = baseRx * scaleX;
    const r2Base = baseRy * scaleY;
    const rand = seedRand(seed);
    const noiseN = 14;
    const noise = Array.from({ length: noiseN }, () => (rand() - 0.5) * 2);
    const sampleNoise = (t) => {
      const x = t * noiseN;
      const i0 = Math.floor(x) % noiseN;
      const i1 = (i0 + 1) % noiseN;
      const f = x - Math.floor(x);
      const f2 = (1 - Math.cos(f * Math.PI)) / 2;
      return noise[i0] * (1 - f2) + noise[i1] * f2;
    };

    const segs = 110;
    const start = (startAngle * Math.PI) / 180;
    const total = (sweepDeg * Math.PI) / 180;

    let d = "";
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const ang = start + total * t;
      // Outward-biased jitter so the loop never wobbles closer to text
      const n = Math.abs(sampleNoise(t));
      const m = Math.abs(sampleNoise((t + 0.41) % 1));
      const r1 = r1Base + n * jitter;
      const r2 = r2Base + m * jitter;
      // Ellipse point, then rotate by tilt, then translate by loop center offset
      const ex = r1 * Math.cos(ang);
      const ey = r2 * Math.sin(ang);
      const rx = ex * cosT - ey * sinT;
      const ry = ex * sinT + ey * cosT;
      const x = cx + dx + rx;
      const y = cy + dy + ry;
      d += (i === 0 ? "M " : " L ") + x.toFixed(2) + "," + y.toFixed(2);
    }
    return d;
  };

  // THREE distinct passes — different centers, tilts, scales & start angles.
  // Centers shifted within ±5px, ellipse tilted ±5°, each pass overshoots a
  // little past 360°. All scaleX/scaleY ≥ 1.0 → never crosses text.
  const LOOPS = [
    { d: buildLoop({ dx: -3, dy:  1, scaleX: 1.00, scaleY: 1.00, tilt: -4,
                     startAngle: -98,  sweepDeg: 372, jitter: 4.5, seed: 1731 }),
      stroke: "#1B3478", opacity: 0.95, width: 2.0 },
    { d: buildLoop({ dx:  4, dy: -2, scaleX: 1.03, scaleY: 1.02, tilt:  3,
                     startAngle: -72,  sweepDeg: 388, jitter: 5.0, seed: 4217 }),
      stroke: "#1F3A8A", opacity: 0.78, width: 1.7 },
    { d: buildLoop({ dx: -1, dy:  4, scaleX: 1.05, scaleY: 1.04, tilt: -6,
                     startAngle: -125, sweepDeg: 395, jitter: 5.5, seed: 9043 }),
      stroke: "#22408F", opacity: 0.55, width: 1.5 },
  ];

  return (
    <div
      data-testid="pen-circled-note"
      style={{
        position: "relative",
        margin: "0 auto",
        maxWidth: "380px",
        padding: `${padY}px ${padX}px`,
      }}
    >
      <svg
        aria-hidden="true"
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        {LOOPS.map((p, idx) => (
          <path
            key={idx}
            d={p.d}
            fill="none"
            stroke={p.stroke}
            strokeOpacity={p.opacity}
            strokeWidth={p.width}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      {/* Reflection — bigger, darker, biblical serif, with a luminous glow
          backdrop directly behind the text so the words feel lifted off the
          page as if light is pushing through them. */}
      <div
        ref={containerRef}
        data-testid="verse-explanation"
        style={{ position: "relative", zIndex: 1 }}
      >
        {/* Soft cream luminous halo behind the text */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            inset: "-12px -8px",
            zIndex: -1,
            background:
              "radial-gradient(60% 80% at 50% 50%, rgba(255,250,235,0.96) 0%, rgba(255,250,235,0.85) 35%, rgba(255,250,235,0.55) 65%, rgba(255,250,235,0) 100%)",
            filter: "blur(6px)",
            pointerEvents: "none",
          }}
        />
        {paragraphs.map((p, i) => (
          <p
            key={i}
            className="font-garamond"
            style={{
              fontSize: "22px",
              lineHeight: 1.5,
              color: "#05030A",
              fontWeight: 500,
              textAlign: "left",
              margin: i === 0 ? "0" : "10px 0 0 0",
              textShadow:
                "0 0 14px rgba(255,250,235,1), 0 0 28px rgba(255,250,235,0.9), 0 0 46px rgba(255,250,235,0.7), 0 1px 0 rgba(255,250,235,0.95)",
              position: "relative",
            }}
          >
            {p}
          </p>
        ))}
      </div>
    </div>
  );
};

